import { useState, useEffect, useCallback, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from '../ApplicationState';
import SpinnerOverlay from '../SpinnerOverlay';
import CheckInTool from '../components/CheckInTool';
import Button from '../components/Button';
import CheckInToolMobile from '../components/CheckInToolMobile';
import StudentHeroSegment from '../components/StudentHeroSegment';
import ReviewCheckIn from './ReviewCheckIn';
import CheckinLimitModal from './CheckinLimitModal';
import {
  listStudentAssignments,
  recordMiddleSchoolJournalEntry,
  retrieveAssignment,
  submitAssignment,
} from '../BackendInterface';
import { getMiddleSchoolJournals } from '../BackendInterface';
import { setLocalStorage, getLocalStorage, removeLocalStorage } from '../aaa';
import { isCheckInModule } from '../GlobalFunctions';
import { selectClassroom } from '../Classrooms';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { State, play, setMedia } from '../components/AudioPlayer/AudioPlayer';
import { Icon } from '@iconify/react';
import toDoHeader from '../images/ToDo_Page.png';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import { values } from './checkInValues';
import styles from './CheckIn.module.scss';

interface LocationState extends Location {
  status: string;
}

/* ================= RATE LIMITING CONSTANTS & HELPERS ================= */

const MAX_SUBMISSIONS_PER_HOUR = 3;
const getRateLimitKey = (userId: string): string => `checkinSubmissionTimestamps:${userId}`;

/**
 * Retrieve the list of timestamp numbers from localStorage.
 * These represent previous submission times.
 */
const getSubmissionTimestamps = (userId: string): number[] => {
  try {
    const raw = window.localStorage.getItem(getRateLimitKey(userId));
    if (!raw) return [];
    return JSON.parse(raw) as number[];
  } catch {
    return [];
  }
};

/**
 * Save the array of timestamp numbers to localStorage.
 * @param timestamps - array of submission timestamps (in ms)
 */
const saveSubmissionTimestamps = (userId: string, timestamps: number[]): void => {
  try {
    window.localStorage.setItem(getRateLimitKey(userId), JSON.stringify(timestamps));
  } catch (err) {
    console.warn('Failed to save submission timestamps', err);
  }
};

/**
 * Determine if the user can submit a new emotion,
 * based on number of submissions within the last rolling hour.
 */
const canSubmitEmotion = (userId: string): boolean => {
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;

  const recent = getSubmissionTimestamps(userId).filter((ts) => ts > oneHourAgo);

  saveSubmissionTimestamps(userId, recent);
  return recent.length < MAX_SUBMISSIONS_PER_HOUR;
};

/**
 * Record a new submission timestamp in localStorage.
 */
const recordEmotionSubmission = (userId: string): void => {
  const timestamps = getSubmissionTimestamps(userId).slice(-10);
  timestamps.push(Date.now());
  saveSubmissionTimestamps(userId, timestamps);
};

const REASON_OPTIONS = [
  'Family',
  'Friends',
  'Teacher',
  'Classmates',
  'Homework/Exam',
  'Work',
  'Health',
  'Energy Level',
  'Not Sure',
];

const DARK_TEXT_COLORS = new Set(['#daeff1', '#fef8d8', '#f5c4b1']);

const getContrastColor = (hexColor: string): string => {
  return DARK_TEXT_COLORS.has(hexColor.toLowerCase()) ? '#43484d' : '#fff';
};

/* ===================================================================== */

const CheckIn = (): JSX.Element => {
  const [isJournalEntriesLoaded, setIsJournalEntriesLoaded] = useState<boolean>(false);
  const [isCheckInAssignmentLoaded, setIsCheckInAssignmentLoaded] = useState<boolean>(false);
  const [isAssignmentSubmissionLoaded, setIsAssignmentSubmissionLoaded] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  // isRateLimited - indicates if user hit submission limit
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);

  // isCheckingIn - show the check in tool
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
  // isSelectingReason - show the reason selection step after emotion is chosen
  const [isSelectingReason, setIsSelectingReason] = useState<boolean>(false);
  // isReviewing - show the response for the daily check in OR student's submission for the check in assignment
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  // isViewingSubmission - show student's submission for the check in assignment
  const [isViewingSubmission, setIsViewingSubmission] = useState<boolean>(false);
  const [toDoHeaderImage, setToDoHeaderImage] = useState<string>('');
  const [selectedEmotion, setSelectedEmotion] = useState<string>('');
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [submittedReasons, setSubmittedReasons] = useState<string[][]>([]);
  const [journalEntries, setJournalEntries] = useState<string[]>([]);
  const [weekCheckInDays, setWeekCheckInDays] = useState<boolean[]>([false, false, false, false, false, false, false]);
  const [checkInAssignment, setCheckInAssignment] = useState<Assignment | undefined>(undefined);
  const [assignmentSubmission, setAssignmentSubmission] = useState<string[] | undefined>(undefined);
  const [timeSubmitted, setTimeSubmitted] = useState<number | undefined>(undefined);

  const { userId, media, isPlayerLoading } = useSelector((state: RootState) => ({
    userId: state.user.userProfile?.userId ? state.user.userProfile.userId : '',
    media: state.audioPlayer.media,
    isPlayerLoading: state.audioPlayer.state === State.loading,
  }));

  const classroom = useSelector((state: RootState) => {
    return selectClassroom(state);
  });

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  const language: string = classroom?.language || 'en';
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const isSmall = useMediaQuery({ query: '(max-width: 820px)' });

  /**
   * Set the correct To-Do header image based on user type and grade.
   */
  const getToDoHeaderImage = useCallback(async (): Promise<void> => {
    try {
      if (userProfile && userProfile.type === 'Teacher' && classroom) {
        if (classroom.grade > 8) {
          setToDoHeaderImage(toDoHeaderHS);
        } else {
          setToDoHeaderImage(toDoHeader);
        }
      } else if (userProfile && userProfile.type === 'Student') {
        if (userProfile.grade > 8) {
          setToDoHeaderImage(toDoHeaderHS);
        } else {
          setToDoHeaderImage(toDoHeader);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [userProfile, classroom]);

  /**
   * Check if currently loaded media is the "Check-In" audio.
   */
  const isCurrentStoryMediaLoaded = (): boolean => media?.title === 'Check-In';

  /**
   * Dispatch action to set the "Check-In" audio media.
   */
  const setCheckInMedia = (): void => {
    dispatch(
      setMedia({
        sources: {
          mp3: 'https://peekaplatform.s3.amazonaws.com/assets/premium/storyAudioMiddleSchool/Check-In-Wheel.wav',
        },
        title: 'Check-In',
      }),
    );
  };

  /**
   * Play the check-in audio if not already loaded.
   */
  const handlePlayAudio = (): void => {
    if (!isCurrentStoryMediaLoaded()) {
      setCheckInMedia();
    }
    dispatch(play());
  };

  /**
   * Store a localStorage flag that user completed or opted out of the check-in,
   * which expires at the end of the current day.
   */
  const storeIsCompletedOrOptedOutFlag = (): void => {
    const now: Date = new Date();
    const hours: number = now.getHours();
    const minutes: number = now.getMinutes();
    const minutesPassed: number = hours * 60 + minutes;
    const minutesPerDay: number = 24 * 60;
    const minutesRemaining: number = minutesPerDay - minutesPassed;
    setLocalStorage('isCheckInCompleteOrOptedOut', 'true', minutesRemaining);
  };

  /**
   * Handle when a user clicks an emotion from the wheel.
   * Resets error and rate-limit flags.
   */
  const handleEmotionClick = (emotion: string): void => {
    setSelectedEmotion(emotion);
    setSelectedReasons([]);
    setIsError(false);
    setIsRateLimited(false);
  };

  /**
   * Navigate user back to their To-Do list.
   */
  const handleGoHome = (): void => {
    history.push('/studentportal/toDos');
  };

  /**
   * Reset the submission state to allow user to submit again.
   */
  const handleResetSubmission = (): void => {
    setIsCheckingIn(true);
    setIsSelectingReason(false);
    setIsReviewing(false);
    setSelectedEmotion('');
    setSelectedReasons([]);
    removeLocalStorage('isCheckInCompleteOrOptedOut');
  };

  /**
   * Handle when user chooses to skip today's check-in.
   */
  const handleSkipToday = (): void => {
    storeIsCompletedOrOptedOutFlag();
    history.push('/studentportal/toDos');
  };

  /**
   * Retrieve a student's assignment submission from backend.
   */
  const retrieveAssignmentSubmission = async (userId: string, createdAt: number): Promise<void> => {
    try {
      const toDoPayload = await retrieveAssignment(userId, createdAt);
      if (toDoPayload && toDoPayload[0] && toDoPayload[0].work && toDoPayload[0].work.content) {
        const content: string[] = toDoPayload[0].work.content;
        setAssignmentSubmission(content);
        setTimeSubmitted(toDoPayload[0].modifiedAt);
      }
    } catch {
      console.error('could not retrieve check in assignment');
    } finally {
      setIsAssignmentSubmissionLoaded(true);
    }
  };

  /**
   * Retrieve journal entries for the current user from backend.
   */
  const retrieveJournalEntries = async (): Promise<void> => {
    try {
      const journalEntriesData = await getMiddleSchoolJournals(userId);
      const now: Date = new Date();
      const today: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Start of the current week (Sunday)
      const startOfWeek: Date = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());

      // End of the current week (end of Saturday)
      const endOfWeek: Date = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);

      // Build a Set of day-of-week indices (0=Sun…6=Sat) that have at least one check-in this week
      const checkedInDays = new Set<number>();
      const emotions: string[] = [];
      const reasonsByEmotion: string[][] = [];

      journalEntriesData.forEach((curr) => {
        const entryDate = new Date(curr.createdAt);
        if (entryDate >= startOfWeek && entryDate < endOfWeek) {
          checkedInDays.add(entryDate.getDay());
        }
        if (entryDate >= today) {
          emotions.push(curr.feeling);
          // reasons is stored as a comma-separated string e.g. "Work, School"
          const reasons: string[] = curr.reasons
            ? curr.reasons
                .split(',')
                .map((r: string) => r.trim())
                .filter(Boolean)
            : [];
          reasonsByEmotion.push(reasons);
        }
      });

      setJournalEntries(emotions);
      setSubmittedReasons(reasonsByEmotion);
      setWeekCheckInDays([0, 1, 2, 3, 4, 5, 6].map((d) => checkedInDays.has(d)));
    } finally {
      setIsJournalEntriesLoaded(true);
    }
  };

  /**
   * Handle "Next" click on emotion wheel — validates emotion then moves to reason step.
   */
  const handleNext = (): void => {
    if (!selectedEmotion) {
      setIsError(true);
      return;
    }
    setIsError(false);
    setIsCheckingIn(false);
    setIsSelectingReason(true);
  };

  /**
   * Handle selecting a reason — toggles selection, max 2 at a time.
   */
  const handleReasonClick = (reason: string): void => {
    setSelectedReasons((prev) => {
      if (prev.includes(reason)) return prev.filter((r) => r !== reason);
      if (prev.length >= 2) return prev;
      return [...prev, reason];
    });
  };

  /**
   * Submit the selected journal entry after validation and rate limit check.
   */
  const submitJournalEntry = async (): Promise<void> => {
    setIsShowSpinner(true);

    try {
      if (!selectedEmotion) {
        setIsError(true);
        return;
      }

      // Rate-limit check first
      if (!canSubmitEmotion(userId)) {
        setIsRateLimited(true);
        return;
      }

      // Record journal entry — reasons formatted as "Work, School" per backend contract
      const reasonString = selectedReasons.join(', ');
      await recordMiddleSchoolJournalEntry(userId, selectedEmotion, '', reasonString, false);

      // Record submission once, after success
      recordEmotionSubmission(userId);

      storeIsCompletedOrOptedOutFlag();

      journalEntries.push(selectedEmotion);
      // Mark today as checked in for the week strip
      setWeekCheckInDays((prev) => {
        const updated = [...prev];
        updated[new Date().getDay()] = true;
        return updated;
      });
      setSubmittedReasons((prev) => [...prev, selectedReasons]);

      if (checkInAssignment && checkInAssignment.status !== 'submitted') {
        const content: string[] =
          selectedReasons.length > 0 ? [selectedEmotion, ...selectedReasons] : [selectedEmotion];
        await submitAssignment(userId, checkInAssignment.createdAt, content);
        setAssignmentSubmission(content);
        setIsViewingSubmission(true);
      } else {
        setIsViewingSubmission(false);
      }

      setIsSelectingReason(false);
      setIsReviewing(true);
    } catch (err) {
      console.error('could not submit emotion to database', err);
    } finally {
      setIsShowSpinner(false);
    }
  };

  /**
   * Retrieve the student's check-in assignment to display.
   */
  const getCheckInAssignment = async (): Promise<void> => {
    setIsShowSpinner(true);
    try {
      if (userProfile) {
        const studentToDos = await listStudentAssignments(userId, language, userProfile.grade);
        const toDo = studentToDos.find((toDo) => isCheckInModule(toDo.todo));
        if (toDo) {
          setCheckInAssignment(toDo);
          await retrieveAssignmentSubmission(userId, toDo.createdAt);
        }
      }
    } catch {
      console.error('Error fetching student ToDos');
    } finally {
      setIsCheckInAssignmentLoaded(true);
      setIsShowSpinner(false);
    }
  };

  /**
   * Determines if user should see the check-in tool or review view based on state.
   */
  const CheckIsCheckingIn = (): void => {
    // if is daily check in, and hasn't submitted any journal entry for today
    // OR
    // if is check in assignment, and hasn't submitted
    if ((!location.state && !journalEntries) || (location.state && location.state.status !== 'submitted')) {
      setIsCheckingIn(true);
      setIsSelectingReason(false);
      setIsReviewing(false);
    }
  };

  const CheckIsReviewing = (): void => {
    // if is daily check in, and has already checked in for today
    // OR
    // if is check in assignment, and has already submitted
    if ((!location.state && journalEntries.length > 0) || (location.state && location.state.status === 'submitted')) {
      setIsReviewing(true);
      setIsCheckingIn(false);
    }
  };

  /* ------------ useEffect hooks to control component lifecycle ------------ */

  // Fetch data once userId is available
  useEffect(() => {
    if (!userId) return;
    if (userProfile && userProfile.type === 'Teacher') {
      setIsLoaded(true);
    } else {
      retrieveJournalEntries();
      getCheckInAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Mark as loaded once all three flags are true
  useEffect(() => {
    if (isJournalEntriesLoaded && isCheckInAssignmentLoaded && isAssignmentSubmissionLoaded) {
      setIsLoaded(true);
    }
  }, [isJournalEntriesLoaded, isCheckInAssignmentLoaded, isAssignmentSubmissionLoaded]);

  useEffect(() => {
    const hasCheckedIn = getLocalStorage('isCheckInCompleteOrOptedOut');
    if (hasCheckedIn) {
      setIsCheckingIn(false);
      setIsReviewing(true);
    } else {
      setIsCheckingIn(true);
      setIsReviewing(false);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      CheckIsCheckingIn();
      CheckIsReviewing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useEffect(() => {
    if (location.state && location.state.status === 'submitted' && assignmentSubmission) {
      setIsViewingSubmission(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentSubmission]);

  useEffect(() => {
    if (!toDoHeaderImage) getToDoHeaderImage();
  }, [classroom, userProfile, toDoHeaderImage, getToDoHeaderImage]);

  /* --------------------------- JSX Render --------------------------- */

  return (
    <>
      <SpinnerOverlay isShow={isShowSpinner || !toDoHeaderImage} />
      <StudentHeroSegment home backgroundImage={toDoHeaderImage}>
        <div className={styles.heroText}>
          <h1>Check In</h1>
          <h3>{`Check in with how you're feeling today`}</h3>
        </div>
      </StudentHeroSegment>
      <div className={styles.container}>
        {/* Rate limit modal — rendered at container level so it works from any step */}
        <CheckinLimitModal
          isShow={isRateLimited}
          onHide={() => setIsRateLimited(false)}
          onOkay={() => setIsRateLimited(false)}
        />
        {isCheckingIn && (
          <Fragment key={'emotion wheel'}>
            <div className={styles.titleRow}>
              <h4>How are you feeling today?</h4>
              <button onClick={handlePlayAudio} disabled={isPlayerLoading}>
                <Icon className={styles.playIcon} icon='ooui:volume-up-ltr' />
              </button>
            </div>
            <p>Take a moment to reflect on your feelings, then select an emotion from the wheel below.</p>
            {isSmall ? (
              <CheckInToolMobile handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            ) : (
              <CheckInTool handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            )}
            <div className={styles.selectedEmotionRow}>
              {selectedEmotion ? (
                <span
                  className={styles.selectedEmotionBadge}
                  style={{
                    backgroundColor:
                      values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                    color: getContrastColor(
                      values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                    ),
                  }}
                >
                  {selectedEmotion}
                </span>
              ) : (
                <span className={styles.selectedEmotionPlaceholder}>&nbsp;</span>
              )}
              {isError && <span className={styles.errorText}>Please select an emotion before continuing</span>}
            </div>
            <div className={styles.nextButtonWrapper}>
              <Button thinText darkBlue small onClick={handleNext}>
                Next
              </Button>
            </div>
            <div className={styles.skipEntry} onClick={handleSkipToday}>
              Skip
            </div>
            <div className={styles.disclaimer}>Disclaimer: The check-in tool is not used for assessment purposes</div>
          </Fragment>
        )}
        {isSelectingReason && (
          <Fragment key={'reason selection'}>
            <div className={styles.reasonTitleRow}>
              <h4>
                {`What's causing you to feel `}
                <span
                  className={styles.emotionBadge}
                  style={{
                    backgroundColor:
                      values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                    color: getContrastColor(
                      values.find((v) => v.subEmotions.includes(selectedEmotion))?.subEmotionColor || '#e0e0e0',
                    ),
                  }}
                >
                  {selectedEmotion}
                </span>
                {` ?`}
              </h4>
            </div>
            <div className={styles.reasonGrid}>
              {REASON_OPTIONS.map((reason) => (
                <button
                  key={reason}
                  className={`${styles.reasonButton} ${selectedReasons.includes(reason) ? styles.reasonButtonSelected : ''}`}
                  onClick={() => handleReasonClick(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
            <div className={styles.doneButtonRow}>
              <button className={styles.doneButton} onClick={submitJournalEntry}>
                Done
              </button>
            </div>
            <div
              className={styles.skipEntry}
              onClick={() => {
                setIsSelectingReason(false);
                setIsCheckingIn(true);
                setSelectedReasons([]);
              }}
            >
              Go Back
            </div>
            <div className={styles.disclaimer}>Disclaimer: The check-in tool is not used for assessment purposes</div>
          </Fragment>
        )}
        {isReviewing && (
          <ReviewCheckIn
            studentWork={assignmentSubmission}
            journalEntries={journalEntries}
            selectedReasons={submittedReasons}
            weekCheckInDays={weekCheckInDays}
            handleResetSubmission={handleResetSubmission}
            handleGoHome={handleGoHome}
            isViewDailyCheckIn={!location.state && !isViewingSubmission}
            isViewAssignmentSubmission={isViewingSubmission}
            timeSubmitted={timeSubmitted}
          />
        )}
      </div>
    </>
  );
};

export default CheckIn;
